import React, { useState, useEffect, useCallback, useRef } from 'react';
import { tokens as tk } from './theme';
import SignaturePad from './components/SignaturePad';

const API = 'https://tcio-api.onrender.com';
const DOT = '\u00B7', DASH = '\u2014', CHECK = '\u2713';
const card = { background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, boxShadow: '0 2px 8px rgba(34,48,62,0.06)' };
const lab = { fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: tk.textSecondary, fontWeight: 600 };

function useIsWide() {
  const [w, setW] = useState(typeof window !== 'undefined' && window.innerWidth >= 900);
  useEffect(() => { const h = () => setW(window.innerWidth >= 900);
    window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return w;
}

// Sections derive from field keys (schema is data; nothing here names a specific field)
const SECTION_RULES = [
  ['Employment history', k => /^emp\d_|^ever_terminated|^terminated_|^employment_gaps|^other_skills/.test(k)],
  ['Education', k => /^edu_/.test(k)],
  ['References', k => /_ref_\d/.test(k)],
  ['Voluntary self-identification', k => /^eeo_/.test(k)],
  ['Applicant statements', k => /^agree_/.test(k)],
  ['General questions', k => /^used_other_name|^name_|^worked_here|^friends_relatives|^available|^days_hours|^availability|^reliable_transport|^can_travel|^can_relocate|^at_least_18|^right_to_work|^essential_functions/.test(k)],
];
function sectionOf(key, i) {
  if (i < 7 && !SECTION_RULES.some(([, t]) => t(key))) return 'About you';
  const hit = SECTION_RULES.find(([, t]) => t(key));
  return hit ? hit[0] : 'Application';
}
const SECTION_ORDER = ['About you', 'Employment history', 'Education', 'References', 'General questions', 'Applicant statements', 'Voluntary self-identification', 'Application'];

export default function ApplyPage({ token }) {
  const isWide = useIsWide();
  const [state, setState] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [formVals, setFormVals] = useState({});
  const [sigName, setSigName] = useState('');
  const [sigImg, setSigImg] = useState(null);
  const [padKey, setPadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [missingKeys, setMissingKeys] = useState([]);
  const [justFinished, setJustFinished] = useState(false);
  const [saveState, setSaveState] = useState('');
  const [declineNote, setDeclineNote] = useState(null);
  const dirty = useRef(false); const saveTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const j = await fetch(API + '/api/apply/' + token).then(r => r.json());
      if (!j.ok) { setErr(j.error || 'This link is invalid.'); setLoading(false); return; }
      setState(j); setFormVals(j.answers || {});
    } catch (e) { setErr('Could not reach the server. Check your connection and reload.'); }
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  // Draft autosave: 1.5s after the last change, only before submission
  useEffect(() => {
    if (!state || state.submitted_at || !dirty.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState('unsaved');
    saveTimer.current = setTimeout(async () => {
      try {
        const j = await fetch(API + '/api/apply/' + token + '/save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ answers: formVals }) }).then(r => r.json());
        setSaveState(j.ok ? 'saved' : 'error');
      } catch (e) { setSaveState('error'); }
    }, 1500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [formVals, state, token]);

  const btn = (primary, disabled) => ({ font: 'inherit', fontSize: 15, fontWeight: 600, padding: '13px 18px', borderRadius: tk.radiusControl,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1, width: '100%', minHeight: 48,
    border: '1px solid ' + (primary ? tk.ai : tk.border), background: primary ? tk.ai : tk.surface, color: primary ? tk.surface : tk.ink });
  const inp = { font: 'inherit', fontSize: 16, padding: '12px 14px', borderRadius: tk.radiusControl, border: '1px solid ' + tk.border,
    color: tk.ink, background: tk.surface, width: '100%', boxSizing: 'border-box' };

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', background: tk.canvas, fontFamily: tk.font, padding: '18px 14px 60px' }}>
      <div style={{ maxWidth: isWide ? 1040 : 560, margin: '0 auto' }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: tk.ink, letterSpacing: '-0.3px', marginBottom: 2 }}>TCIO</div>
        {children}
      </div>
    </div>);

  if (loading) return wrap(<div style={{ ...card, height: 160, background: tk.surfaceSoft, marginTop: 14 }} />);
  if (err) return wrap(
    <div style={{ ...card, padding: 20, marginTop: 14 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: tk.ink, marginBottom: 6 }}>Link problem</div>
      <div style={{ fontSize: 14, color: tk.textSecondary }}>{err}</div>
    </div>);

  const schema = state.form_schema || {};
  const fields = Array.isArray(schema.fields) ? schema.fields : [];
  const required = fields.filter(f => f.required);
  const filled = (f) => { const v = formVals[f.key]; return !(v === undefined || v === null || v === '' || v === false); };
  const done = required.filter(filled).length;
  const pct = required.length ? Math.round(done / required.length * 100) : 0;
  const submitted = !!state.submitted_at || justFinished;

  if (submitted && state.offer) {
    const o = state.offer;
    const doAccept = async () => {
      if (!sigName.trim()) { setFormErr('Type your full legal name to accept.'); return; }
      if (!sigImg) { setFormErr('Draw your signature in the box.'); return; }
      setBusy(true); setFormErr('');
      try { const j = await fetch(API + '/api/apply/' + token + '/offer/accept', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ signature_name: sigName.trim(), signature_image: sigImg }) }).then(r => r.json());
        if (j.ok) { await load(); window.scrollTo(0, 0); } else setFormErr(j.error || 'Could not accept. Try again.'); } catch (e) { setFormErr('Connection problem. Try again.'); }
      setBusy(false);
    };
    const doDecline = async () => {
      setBusy(true); setFormErr('');
      try { const j = await fetch(API + '/api/apply/' + token + '/offer/decline', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: declineNote || '' }) }).then(r => r.json());
        if (j.ok) { setDeclineNote(null); await load(); window.scrollTo(0, 0); } else setFormErr(j.error || 'Could not send. Try again.'); } catch (e) { setFormErr('Connection problem. Try again.'); }
      setBusy(false);
    };
    return wrap(
      <div>
        <div style={{ fontSize: 13, color: tk.textSecondary, marginBottom: 12 }}>{state.candidate.first_name} {DOT} {state.org_name}</div>
        <div style={{ ...card, padding: 18, marginBottom: 12 }}>
          <div style={{ ...lab, marginBottom: 4 }}>{o.status === 'accepted' ? 'Offer accepted' : o.status === 'declined' ? 'Offer declined' : 'Employment offer'}</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: tk.ink, lineHeight: 1.3, marginBottom: 10 }}>{o.position} {DOT} starts {o.start_date ? new Date(String(o.start_date).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}</div>
          <div style={{ border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, background: '#fff', padding: '14px 16px', maxHeight: '55vh', overflowY: 'auto', marginBottom: 12, fontSize: 13.5, lineHeight: 1.55, color: tk.ink, whiteSpace: 'pre-wrap' }}>{o.letter_body}</div>
          {o.status === 'accepted' && <div style={{ fontSize: 14, color: tk.success, fontWeight: 600 }}>{CHECK} You accepted this offer{o.signed_by ? ' as ' + o.signed_by : ''}{o.responded_at ? ' on ' + new Date(o.responded_at).toLocaleDateString() : ''}. {state.org_name} will send your onboarding paperwork next. You can close this page.</div>}
          {o.status === 'declined' && <div style={{ fontSize: 14, color: tk.textSecondary }}>You declined this offer{o.responded_at ? ' on ' + new Date(o.responded_at).toLocaleDateString() : ''}. Thank you for your time.</div>}
          {o.status === 'extended' && (
            <div>
              {o.accept_jurat && <div style={{ fontSize: 12.5, color: tk.ink, background: tk.canvas, border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, padding: '10px 12px', marginBottom: 12, lineHeight: 1.5 }}>{o.accept_jurat}</div>}
              {declineNote === null ? (
                <div>
                  <div style={{ ...lab, marginBottom: 4 }}>Full legal name</div>
                  <input style={inp} value={sigName} autoComplete="name" placeholder={state.candidate.first_name + ' ' + state.candidate.last_name} onChange={e => setSigName(e.target.value)} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '12px 0 4px' }}>
                    <span style={lab}>Draw your signature</span>
                    <button style={{ font: 'inherit', fontSize: 12, fontWeight: 600, color: tk.textSecondary, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={() => { setSigImg(null); setPadKey(k => k + 1); }}>Clear</button>
                  </div>
                  <div style={{ border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, overflow: 'hidden', background: '#fff' }}><SignaturePad key={padKey} onChange={setSigImg} height={130} /></div>
                  {formErr && <div style={{ fontSize: 13, color: tk.danger, marginTop: 8 }}>{formErr}</div>}
                  <div style={{ marginTop: 12 }}><button style={btn(true, busy)} disabled={busy} onClick={doAccept}>{busy ? 'Sending\u2026' : 'Accept and sign'}</button></div>
                  <div style={{ marginTop: 10, textAlign: 'center' }}><button style={{ font: 'inherit', fontSize: 13, color: tk.textSecondary, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setDeclineNote('')}>I need to decline this offer</button></div>
                </div>
              ) : (
                <div>
                  <div style={{ ...lab, marginBottom: 4 }}>Anything you want the hiring team to know? (optional)</div>
                  <textarea rows={3} style={{ ...inp, resize: 'vertical' }} value={declineNote} onChange={e => setDeclineNote(e.target.value)} />
                  {formErr && <div style={{ fontSize: 13, color: tk.danger, marginTop: 8 }}>{formErr}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button style={btn(false, busy)} disabled={busy} onClick={doDecline}>{busy ? 'Sending\u2026' : 'Decline offer'}</button>
                    <button style={btn(false, false)} onClick={() => { setDeclineNote(null); setFormErr(''); }}>Back</button>
                  </div>
                </div>)}
            </div>)}
        </div>
        <div style={{ fontSize: 11.5, color: tk.textMuted, marginTop: 14, textAlign: 'center' }}>Questions about the offer? Call the number in the letter. This link is private to you {DASH} don't forward it.</div>
      </div>);
  }

  if (submitted) return wrap(
    <div style={{ ...card, padding: 24, marginTop: 14, textAlign: 'center' }}>
      <div style={{ fontSize: 40, color: tk.success }}>{CHECK}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: tk.ink, margin: '6px 0' }}>Thanks, {state.candidate.first_name}.</div>
      <div style={{ fontSize: 14, color: tk.textSecondary }}>Your application for {state.position} at {state.org_name} {justFinished ? 'is in' : 'was received' + (state.submitted_at ? ' ' + new Date(state.submitted_at).toLocaleDateString() : '')}. The hiring team will be in touch. You can close this page.</div>
    </div>);

  const setVal = (k, val) => { dirty.current = true; setFormVals(prev => ({ ...prev, [k]: val })); };
  const setFV = (k) => (e) => setVal(k, e.target.type === 'checkbox' ? e.target.checked : e.target.value);
  const renderField = (f) => {
    const v = formVals[f.key];
    const miss = missingKeys.includes(f.key);
    const base = { ...inp, fontSize: 15, borderColor: miss ? tk.danger : tk.border };
    const isAgreement = f.type === 'checkbox' && /^agree_/.test(f.key);
    return (
      <div key={f.key} id={'f-' + f.key} style={{ marginBottom: isAgreement ? 14 : 12 }}>
        {isAgreement
          ? <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13.5, lineHeight: 1.5, color: miss ? tk.danger : tk.ink, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!v} onChange={setFV(f.key)} style={{ marginTop: 4, flexShrink: 0 }} />
              <span>{f.label}{f.required ? ' *' : ''}</span>
            </label>
          : <div style={{ ...lab, marginBottom: 4, color: miss ? tk.danger : tk.textSecondary }}>{f.label}{f.required ? ' *' : ''}</div>}
        {isAgreement ? null
        : f.type === 'radio' ? (f.options || []).map(o => (
          <label key={o} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, color: tk.ink, padding: '5px 0', cursor: 'pointer' }}>
            <input type="radio" name={f.key} checked={v === o} onChange={() => setVal(f.key, o)} style={{ marginTop: 3 }} />
            <span>{o}</span>
          </label>))
        : f.type === 'checkbox' ? (
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, color: tk.ink, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!v} onChange={setFV(f.key)} style={{ marginTop: 3 }} />
            <span>Yes</span>
          </label>)
        : f.type === 'date' ? <input type="date" style={base} value={v || ''} onChange={setFV(f.key)} />
        : f.type === 'number' ? <input type="number" inputMode="numeric" step="1" min="0" style={base} value={v === undefined ? '' : v} onChange={setFV(f.key)} />
        : <input style={base} value={v || ''} onChange={setFV(f.key)}
            autoComplete={f.key.includes('address') ? 'street-address' : f.key.includes('phone') ? 'tel' : f.key === 'email' ? 'email' : f.key === 'full_name' ? 'name' : 'off'}
            inputMode={f.key.includes('phone') ? 'tel' : f.key === 'email' ? 'email' : undefined} />}
        {f.help && <div style={{ fontSize: 11.5, color: tk.textMuted, marginTop: 3 }}>{f.help}</div>}
      </div>);
  };

  // Group fields into sections, preserving schema order
  const groups = [];
  fields.forEach((f, i) => {
    const name = sectionOf(f.key, i);
    let g = groups.find(x => x.name === name);
    if (!g) { g = { name, fields: [] }; groups.push(g); }
    g.fields.push(f);
  });
  groups.sort((a, b) => SECTION_ORDER.indexOf(a.name) - SECTION_ORDER.indexOf(b.name));
  const sectionDone = (g) => { const r = g.fields.filter(f => f.required); return r.length ? r.every(filled) : true; };
  const jump = (name) => { const el = document.getElementById('s-' + name.replace(/\s+/g, '-')); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  const doSubmit = async () => {
    const missing = required.filter(f => !filled(f));
    if (missing.length) {
      setMissingKeys(missing.map(f => f.key));
      setFormErr(missing.length + ' required ' + (missing.length === 1 ? 'item is' : 'items are') + ' still blank ' + DASH + ' they are marked in red.');
      const el = document.getElementById('f-' + missing[0].key); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!sigName.trim()) { setFormErr('Type your full legal name to sign.'); return; }
    if (!sigImg) { setFormErr('Draw your signature in the box.'); return; }
    setBusy(true); setFormErr(''); setMissingKeys([]);
    try {
      const j = await fetch(API + '/api/apply/' + token, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: formVals, signature_name: sigName.trim(), signature_image: sigImg }) }).then(r => r.json());
      if (j.ok) { setJustFinished(true); window.scrollTo(0, 0); }
      else setFormErr(j.error || 'Could not submit. Try again.');
    } catch (e) { setFormErr('Connection problem. Try again.'); }
    setBusy(false);
  };

  const sectionList = (
    <div style={isWide ? { ...card, padding: '10px 12px', position: 'sticky', top: 14 } : { ...card, padding: '10px 12px', marginBottom: 12 }}>
      <div style={{ ...lab, padding: '4px 4px 8px' }}>Sections</div>
      {groups.map((g, i) => (
        <div key={g.name} onClick={() => jump(g.name)}
          style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 4px', borderBottom: i < groups.length - 1 ? '1px solid ' + tk.borderSoft : 'none', cursor: 'pointer', fontSize: 13 }}>
          <span style={{ color: tk.ink }}>{i + 1}. {g.name}</span>
          <span style={{ color: sectionDone(g) ? tk.success : tk.textMuted, fontWeight: 700, flexShrink: 0 }}>{sectionDone(g) ? CHECK : '\u2013'}</span>
        </div>))}
      <div style={{ fontSize: 11.5, color: tk.textMuted, padding: '10px 4px 2px' }}>
        {saveState === 'saved' ? 'Draft saved ' + DASH + ' you can come back to this link.' : saveState === 'unsaved' ? 'Saving draft\u2026' : saveState === 'error' ? 'Draft not saved ' + DASH + ' check your connection.' : 'Your progress saves automatically.'}
      </div>
    </div>);

  return wrap(
    <div>
      <div style={{ fontSize: 13, color: tk.textSecondary, marginBottom: 12 }}>{state.candidate.first_name}, you are applying for {state.position} {DOT} {state.org_name}</div>
      <div style={{ ...card, padding: '12px 16px', marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 6 }}>
          <span style={{ fontWeight: 600, color: tk.ink }}>{state.title || 'Job Application'}</span>
          <span style={{ color: tk.textSecondary, fontVariantNumeric: 'tabular-nums' }}>{done} of {required.length} required</span>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: tk.surfaceSoft, overflow: 'hidden' }}>
          <div style={{ width: pct + '%', height: '100%', background: pct === 100 ? tk.success : tk.ai, transition: 'width 0.3s' }} />
        </div>
      </div>

      <div style={isWide ? { display: 'grid', gridTemplateColumns: '62fr 38fr', gap: 16, alignItems: 'start' } : undefined}>
      <div>
        {!isWide && sectionList}
        {schema.note &&
          <div style={{ fontSize: 12.5, color: tk.textSecondary, background: tk.canvas, border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, padding: '9px 11px', marginBottom: 12, lineHeight: 1.5 }}>{schema.note}</div>}
        {groups.map((g, i) => (
          <div key={g.name} id={'s-' + g.name.replace(/\s+/g, '-')} style={{ ...card, padding: 18, marginBottom: 12, scrollMarginTop: 14 }}>
            <div style={{ ...lab, marginBottom: 4 }}>Section {i + 1} of {groups.length}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: tk.ink, lineHeight: 1.3, marginBottom: 12 }}>{g.name}</div>
            {g.name === 'Voluntary self-identification' &&
              <div style={{ fontSize: 12.5, color: tk.textSecondary, marginBottom: 12, lineHeight: 1.5 }}>This section is strictly voluntary and is kept separate from hiring decisions. You may choose not to answer.</div>}
            {g.name === 'Applicant statements' &&
              <div style={{ fontSize: 12.5, color: tk.textSecondary, marginBottom: 12, lineHeight: 1.5 }}>Read each statement and check the box to agree. Each check is your initial on that paragraph.</div>}
            {g.fields.map(renderField)}
          </div>))}

        <div style={{ ...card, padding: 18 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: tk.ink, lineHeight: 1.3, marginBottom: 10 }}>Certification and signature</div>
          {schema.jurat &&
            <div style={{ fontSize: 12.5, color: tk.ink, background: tk.canvas, border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, padding: '10px 12px', marginBottom: 12, lineHeight: 1.5 }}>{schema.jurat}</div>}
          <div style={{ fontSize: 13.5, color: tk.textSecondary, marginBottom: 12 }}>Typing your full legal name and drawing your signature below records your electronic signature on this application under the certification above, with a timestamp.</div>
          <div style={{ ...lab, marginBottom: 4 }}>Full legal name</div>
          <input style={inp} value={sigName} autoComplete="name" placeholder={state.candidate.first_name + ' ' + state.candidate.last_name} onChange={e => setSigName(e.target.value)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '12px 0 4px' }}>
            <span style={lab}>Draw your signature</span>
            <button style={{ font: 'inherit', fontSize: 12, fontWeight: 600, color: tk.textSecondary, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={() => { setSigImg(null); setPadKey(k => k + 1); }}>Clear</button>
          </div>
          <div style={{ border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, overflow: 'hidden', background: '#fff' }}>
            <SignaturePad key={padKey} onChange={setSigImg} height={130} />
          </div>
          {formErr && <div style={{ fontSize: 13, color: tk.danger, marginTop: 8 }}>{formErr}</div>}
          <div style={{ marginTop: 12 }}>
            <button style={btn(true, busy)} disabled={busy} onClick={doSubmit}>{busy ? 'Submitting\u2026' : 'Sign and submit application'}</button>
          </div>
        </div>
      </div>
      {isWide && sectionList}
      </div>
      <div style={{ fontSize: 11.5, color: tk.textMuted, marginTop: 14, textAlign: 'center' }}>Questions? Contact the hiring team at {state.org_name}. This link is private to you {DASH} don't forward it.</div>
    </div>);
}
